import React from "react";
import moment from "moment-timezone";
import Button from "../Button/Button.js";
import { ReactComponent as CoordinatorIcon } from "../../Assets/Images/coordinatoricon.svg";
import { ReactComponent as DateIcon } from "../../Assets/Images/dateicon.svg";
import { ReactComponent as OnlineIcon } from "../../Assets/Images/onlineicon.svg";
import { ReactComponent as NoDetailedView } from "../../Assets/Images/No-detailed-view.svg";
import { ExternalLink, CheckCircle2, MapPin, Star } from "lucide-react";

export default function CandidateInterviewDetails({
  events,
  details,
  candidateName,
  onViewInterview,
  onFeedback,
  canGiveFeedback,
  userId,
}) {
  if (!events.length)
    return (
      <div className="candidate-interview-empty">
        <NoDetailedView
          className="candidate-interview-asset-icon candidate-interview-empty-icon"
          aria-hidden="true"
          focusable="false"
        />
        <h3>No interviews scheduled</h3>
        <p>
          Schedule an interview to see round details and assigned panel members
          here.
        </p>
      </div>
    );

  return (
    <div className="candidate-interview-rounds">
      {events.map((event) => {
        const interview = { ...event, ...details[event.id] };
        const panel = interview.panel || [];
        const timezone = event.timezone || "UTC";
        const status = interview.status || "Scheduled";
        const meetingLink = (interview.meeting_link || interview.meetingLink || "").trim();
        const interviewType = (interview.interview_type || interview.interviewType || "")
          .toLowerCase()
          .replace(/[\s_-]/g, "");
        const isOnline = interviewType === "online" || (
          interviewType !== "offline" && interviewType !== "inperson" && Boolean(meetingLink)
        );
        let safeMeetingLink = null;
        if (isOnline && meetingLink) {
          try {
            const url = new URL(
              /^[a-z][a-z\d+.-]*:/i.test(meetingLink) ? meetingLink : `https://${meetingLink}`,
            );
            if (["https:", "http:"].includes(url.protocol)) safeMeetingLink = url.href;
          } catch {
            // Keep invalid meeting URLs as text instead of a clickable link.
          }
        }
        const address = interview.address || interview.address1 || "Location not provided";
        const addressDetails = [
          interview.address2 || interview.address_2,
          interview.city,
          interview.state,
          interview.zip_code || interview.zipCode,
          interview.country,
        ].filter(Boolean).join(", ");
        return (
          <article className="candidate-interview-round" key={event.id}>
            <header className="candidate-interview-round-header">
              <div>
                <div className="candidate-interview-round-title">
                  <h4>
                    Scheduled for{" "}
                    {event.candidateName ||
                      event.candidate?.name ||
                      interview.candidate_name ||
                      candidateName ||
                      "Candidate"}
                  </h4>
                </div>
                <span className="candidate-interview-status">
                  <CheckCircle2 size="1em" />
                  {status}
                </span>
              </div>
              <div className="candidate-interview-round-actions">
                <Button
                  label="View Interview"
                  event={() => onViewInterview(event)}
                  className="candidate-interview-action"
                  hoverd={false}
                />
              </div>
            </header>
            <div className="candidate-interview-summary">
              <div>
                <span className="candidate-interview-label">
                  <DateIcon
                    className="candidate-interview-asset-icon"
                    aria-hidden="true"
                    focusable="false"
                  />
                  DATE &amp; TIME
                </span>
                <strong>
                  {moment(event.start).tz(timezone).format("YYYY-MM-DD")}
                </strong>
                <small>
                  {moment(event.start).tz(timezone).format("h:mm A")} &mdash;{" "}
                  {moment(event.end).tz(timezone).format("h:mm A")} ({timezone})
                </small>
              </div>
              <div>
                <span className="candidate-interview-label">
                  {isOnline ? (
                    <OnlineIcon
                      className="candidate-interview-asset-icon"
                      aria-hidden="true"
                      focusable="false"
                    />
                  ) : (
                    <MapPin size="1em" />
                  )}
                  FORMAT ({isOnline ? "ONLINE" : "IN-PERSON"})
                </span>
                {safeMeetingLink ? (
                  <a
                    href={safeMeetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Join Video Meeting <ExternalLink size="1em" />
                  </a>
                ) : (
                  <strong>
                    {isOnline
                      ? "Meeting link unavailable"
                      : address}
                  </strong>
                )}
                <small>
                  {isOnline ? meetingLink : addressDetails}
                </small>
              </div>
              <div>
                <span className="candidate-interview-label">
                  <CoordinatorIcon
                    className="candidate-interview-asset-icon"
                    aria-hidden="true"
                    focusable="false"
                  />
                  COORDINATOR
                </span>
                <strong>{event.interviewer_name || "Not assigned"}</strong>
                <small>
                  {interview.company_name ||
                    event.job?.company_name ||
                    event.interviewer_email}
                </small>
              </div>
            </div>
            {panel.length > 0 && (
              <>
                <div className="candidate-interview-panel-heading">
                  <h5>
                    <CoordinatorIcon
                      className="candidate-interview-asset-icon"
                      aria-hidden="true"
                      focusable="false"
                    />
                    ASSIGNED INTERVIEW PANEL ({panel.length})
                  </h5>
                </div>
                <div className="candidate-interview-evaluators">
                  {panel.map((member, memberIndex) => {
                    const name =
                      [member.first_name, member.last_name]
                        .filter(Boolean)
                        .join(" ") ||
                      member.user_name ||
                      "Panel member";
                    const rating = Math.min(
                      5,
                      Math.max(0, Number(member.rating) || 0),
                    );
                    const canEdit =
                      canGiveFeedback &&
                      userId != null &&
                      String(member.user_id) === String(userId);
                    return (
                      <section
                        className="candidate-interview-evaluator"
                        key={member.user_id || memberIndex}
                      >
                        <div className="candidate-interview-member">
                          <div
                            className="candidate-interview-avatar"
                            aria-hidden="true"
                          >
                            {name
                              .split(/\s+/)
                              .slice(0, 2)
                              .map((part) => part[0])
                              .join("")
                              .toUpperCase()}
                          </div>
                          <div className="candidate-interview-member-info">
                            <strong>{name}</strong>
                            {member.designation && (
                              <small>
                                Designation: <span>{member.designation}</span>
                              </small>
                            )}
                            <div>
                              {member.role && (
                                <span className="candidate-interview-role">
                                  {member.role}
                                </span>
                              )}
                              {member.department && (
                                <small> ({member.department})</small>
                              )}
                            </div>
                          </div>
                          {(member.status || member.confirmation_status) && (
                            <span className="candidate-interview-confirmation">
                              {member.confirmation_status || member.status}
                            </span>
                          )}
                        </div>
                        <div className="candidate-interview-feedback">
                          <div>
                            {(() => {
                              const recommendation =
                                member.hiring_recommendation ||
                                member.recommendation ||
                                "";
                              const normalizedRecommendation = recommendation
                                .trim()
                                .toLowerCase()
                                .replace(/\\s+/g, " ");
                              const recommendationColors = {
                                "strong hire": {
                                  background: "#DCFCE7",
                                  color: "#166534",
                                  border: "1px solid #86EFAC",
                                },
                                hire: {
                                  background: "#FEF3C7",
                                  color: "#92400E",
                                  border: "1px solid #FCD34D",
                                },
                                hold: {
                                  background: "#FFEDD5",
                                  color: "#9A3412",
                                  border: "1px solid #FDBA74",
                                },
                                "no hire": {
                                  background: "#FEE2E2",
                                  color: "#991B1B",
                                  border: "1px solid #FCA5A5",
                                },
                              };
                              const pillColors =
                                recommendationColors[normalizedRecommendation] ||
                                {
                                  background: "var(--body__background)",
                                  color: "var(--header__color)",
                                  border: "1px solid var(--border__color)",
                                };

                              return (
                                <>
                                <div style={{display: "flex"}}>
                                  {rating > 0 && (
                                    <div
                                      className="candidate-interview-rating"
                                      aria-label={`${rating} out of 5 stars`}
                                    >
                                      {Array.from({ length: 5 }, (_, star) => (
                                        <Star
                                          key={star}
                                          size="1em"
                                          fill={
                                            star < rating
                                              ? "currentColor"
                                              : "none"
                                          }
                                          className={star < rating ? "rated" : ""}
                                        />
                                      ))}
                                    </div>
                                  )}
                                  {recommendation && (
                                    <span
                                      className="candidate-interview-recommendation-pill"
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        borderRadius: "999px",
                                        padding: "4px 10px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        lineHeight: 1.4,
                                        whiteSpace: "nowrap",
                                        marginLeft: "5px",
                                        ...pillColors,
                                      }}
                                    >
                                      {recommendation}
                                    </span>
                                  )}
                                  </div>
                                  {member.notes && (
                                    <p>&quot;{member.notes}&quot;</p>
                                  )}
                                  {!rating && !recommendation && (
                                    <p>Feedback pending evaluation</p>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          {canEdit && (
                            <Button
                              label={rating ? "Edit Feedback" : "Add Feedback"}
                              event={() => onFeedback(event, member)}
                              className="candidate-interview-action"
                              hoverd={false}
                            />
                          )}
                        </div>
                      </section>
                    );
                  })}
                </div>
              </>
            )}
          </article>
        );
      })}
    </div>
  );
}